import { useMemo } from 'react';
import { Card, Text } from '@billda/design-system';
import { FinancialSummaryRow } from '../FinancialSummaryRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import type {
  PotFinancials,
  FixedRateWorker,
} from '../../types/potDetails.types';

interface PotFinancialBreakdownCardProps {
  financials: PotFinancials;
  fixedRateWorkers: FixedRateWorker[];
}

export function PotFinancialBreakdownCard({
  financials,
  fixedRateWorkers,
}: PotFinancialBreakdownCardProps) {
  const { fixedRateTotal, adHocTotal } = useMemo(() => {
    const gangMembers = fixedRateWorkers.filter(w => w.isGangMember);
    const adhoc = fixedRateWorkers.filter(w => !w.isGangMember);
    return {
      fixedRateTotal: gangMembers.reduce((sum, w) => sum + w.totalAmount, 0),
      adHocTotal: adhoc.reduce((sum, w) => sum + w.totalAmount, 0),
    };
  }, [fixedRateWorkers]);

  return (
    <Card
      data-testid="pot-financial-breakdown-card"
      className="h-full p-4 sm:p-6"
    >
      <Text variant="h6" className="mb-4">
        Financial Summary
      </Text>
      <div className="flex flex-col gap-4">
        <FinancialSummaryRow
          label="Gross Amount"
          value={formatCurrency(financials.grossAmount)}
        />
        {fixedRateTotal > 0 && (
          <FinancialSummaryRow
            label="Fixed Rate Workers"
            value={`-${formatCurrency(fixedRateTotal)}`}
            valueColor="error"
          />
        )}
        {adHocTotal > 0 && (
          <FinancialSummaryRow
            label="Ad-hoc Workers"
            value={`-${formatCurrency(adHocTotal)}`}
            valueColor="error"
          />
        )}
        {financials.totalDeductions > 0 && (
          <FinancialSummaryRow
            label="Deductions"
            value={`-${formatCurrency(financials.totalDeductions)}`}
            valueColor="error"
          />
        )}
        <FinancialSummaryRow
          label="Net Amount"
          value={formatCurrency(financials.netAmount)}
          valueColor="primary"
          emphasis
          dividerAbove
        />
      </div>
    </Card>
  );
}
